import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CiLayout from './ciLayout';
import TableActions from './tableActions';
import ProcessTable from './processTable';
import axios from 'axios';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Componente que exibe a tabela de processos para usuários administradores.
 * @param {Object} props - Propriedades recebidas pelo componente.
 * @param {Object} props.response - Resposta da API com os dados do usuário.
 */
function Ci({ response }) {
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState('');
    const [filteredRows, setFilteredRows] = useState([]);
    const [rows, setRows] = useState([]);

    useEffect(() => {
        if (response.auth === true) {
            setRows(response.resp);
            setFilteredRows(response.resp);
        }
    }, [response]);

    useEffect(() => {
        setFilteredRows(
            rows.filter((row) =>
                Object.values(row).some((value) =>
                    String(value).toLowerCase().includes(searchText.toLowerCase())
                )
            )
        );
    }, [searchText, rows]);

    /**
     * Atualiza o estado do texto de pesquisa conforme o usuário digita.
     * @param {Object} event - Evento de mudança no campo de pesquisa.
     */
    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    /**
     * Remove um processo específico.
     * @param {number|string} id - ID do processo a ser removido.
     */
    const removeProcess = async (id) => {
        try {
            const res = await axios.post(`/api/remove/process/${id}`, null, { withCredentials: true });
            if (res.data.status === 200) {
                navigate('/ci/controle');
            } else {
                alert(res.data.msg);
            }
        } catch (error) {
            console.error('Erro ao remover o processo:', error);
            alert('Erro ao remover o processo');
            navigate('/bad/request');
        }
    };

    /**
     * Copia a tabela inteira para a área de transferência.
     */
    const handleCopyTable = () => {
        const csvData = rows.map((row) =>
            columns.map((col) => row[col.field]).join("\t")
        );
        const csvString = [columns.map((col) => col.headerName).join("\t")]
            .concat(csvData)
            .join("\n");
        navigator.clipboard.writeText(csvString).then(
            () => alert("Tabela copiada para a área de transferência!"),
            (err) => alert("Erro ao copiar a tabela: ", err)
        );
    };

    /**
     * Exporta os dados para um arquivo Excel.
     */
    const handleExportExcel = () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Processos");

        const columnsExcel = columns
            .filter((col) => col.field !== "acao")
            .map((col) => ({
                header: col.headerName,
                key: col.field,
                width: 30,
            }));

        worksheet.columns = columnsExcel;

        rows.forEach((row) => {
            const rowData = {};
            columnsExcel.forEach((col) => {
                rowData[col.key] = row[col.key];
            });
            worksheet.addRow(rowData);
        });

        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF17A2B8" },
            };
            cell.alignment = {
                horizontal: "center",
                wrapText: true,
                vertical: "middle",
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber !== 1) {
                row.eachCell((cell) => {
                    cell.alignment = {
                        wrapText: true,
                        vertical: "center",
                        horizontal: "left",
                    };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                });
            }
        });

        worksheet.eachRow({ includeEmpty: false }, (row) => {
            row.height = 40;
        });

        worksheet.getColumn("data_inicio").numFmt = "dd/mm/yyyy";

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
            });
            saveAs(blob, "processos.xlsx");
        });
    };

    /**
     * Exporta os dados para um arquivo PDF.
     */
    const handleExportPDF = () => {
        const doc = new jsPDF("landscape");

        const columnsPDF = columns
            .filter((col) => col.field !== "acao")
            .map((col) => ({ header: col.headerName, dataKey: col.field }));

        const rowsPDF = rows.map((row) => {
            const rowData = {};
            columns.forEach((col) => {
                if (col.field !== "acao") {
                    rowData[col.field] = row[col.field]
                        ? row[col.field].toString()
                        : "";
                }
            });
            return rowData;
        });

        const columnStyles = {
            id: { cellWidth: 15 },
            processo: { cellWidth: 30 },
            pasta: { cellWidth: 30 },
            data_inicio: { cellWidth: 25 },
            nome_client: { cellWidth: 35 },
            autor: { cellWidth: 35 },
            advAutor: { cellWidth: 35 },
            reu: { cellWidth: 35 },
            advogado: { cellWidth: 35 },
        };

        doc.autoTable({
            columns: columnsPDF,
            body: rowsPDF,
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: "linebreak",
            },
            columnStyles: columnStyles,
            startY: 20,
            margin: { top: 10, left: 10, right: 10, bottom: 10 },
            theme: "grid",
            headStyles: {
                fillColor: [22, 162, 184],
                textColor: [255, 255, 255],
            },
        });

        doc.save("processos-novo.pdf");
    };

    /**
     * Imprime a tabela de processos.
     */
    const handlePrint = () => {
        const printContent = document.getElementById("data-grid");
        const WindowPrt = window.open("", "", "width=900,height=650");
        WindowPrt.document.write(printContent.innerHTML);
        WindowPrt.document.close();
        WindowPrt.focus();
        WindowPrt.print();
        WindowPrt.close();
    };

    // Definição das colunas para o DataGrid
    const columns = [
        { field: "id", headerName: "#", width: 70 },
        {
            field: "processo",
            headerName: "Processo",
            width: 150,
            sortable: true,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "pasta",
            headerName: "Pasta",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "data_inicio",
            headerName: "Início",
            width: 110,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "nome_client",
            headerName: "Cliente",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "autor",
            headerName: "Autor",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "advAutor",
            headerName: "Adv/A",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "reu",
            headerName: "Réu",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "advogado",
            headerName: "Adv/R",
            width: 150,
            renderCell: (params) => (
                <div
                    onClick={() => navigate(`/ci/pagamentos/${params.row.id}`)}
                    style={{ cursor: "pointer", color: "#1a76d2" }}
                >
                    {params.value}
                </div>
            ),
        },
        {
            field: "acao",
            headerName: "Ação",
            width: 100,
            renderCell: (params) => (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <button
                        className="btn btn-danger"
                        onClick={(e) => {
                            e.stopPropagation(); // Evita o clique na linha
                            removeProcess(params.row.id);
                        }}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ),
        },
    ];

    return (
        <CiLayout isAdmin={true}>
            <div className="row" style={{ marginTop: 20 }}>
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Consultar Processos</h3>
                        </div>
                        <div className="card-body">
                            <TableActions
                                onSearchChange={handleSearchChange}
                                onCopyTable={handleCopyTable}
                                onExportExcel={handleExportExcel}
                                onExportPDF={handleExportPDF}
                            />
                            <ProcessTable
                                rows={filteredRows}
                                columns={columns}
                                onRowClick={(params) => navigate(`/ci/pagamentos/${params.id}`)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </CiLayout>
    );
}

export default Ci;
